import React, { useState } from "react";
import { toast } from "sonner";
import { Microphone, Spinner } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import DismissibleIntro from "@/components/DismissibleIntro";
import { CITY_PLACEHOLDER, PROPERTY_TYPES } from "./shared";

// ── Phase 1: Setup ────────────────────────────────────────────────
export default function SetupPhase({ onStart, isStudio, targetPropertyId }) {
  const [city, setCity] = useState("");
  const [address, setAddress] = useState("");
  // iter 242: tipologia opzionale (null = "non specificato, l'agente la dice nel vocale").
  const [ptype, setPtype] = useState(null);
  const [starting, setStarting] = useState(false);

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/sopralluogo/start", {
        city: city.trim() || null,
        address: address.trim() || null,
        property_type: ptype || null,
      });
      onStart(data);
    } catch (e) {
      toast.error(apiError(e, "Impossibile avviare il sopralluogo"));
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto py-2" data-testid="sopralluogo-setup">
      {targetPropertyId && (
        <div className="mb-4 p-3 bg-[#F1F4ED] border border-[#7C8B6E]/30 rounded-lg flex items-start gap-2.5" data-testid="sopralluogo-target-banner">
          <div className="w-7 h-7 rounded-full bg-[#7C8B6E] text-white flex items-center justify-center flex-shrink-0 text-[11px] font-bold">
            ✓
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[12.5px] font-semibold text-[#1C1C1A] leading-tight">
              Stai dettando per un annuncio esistente
            </div>
            <div className="text-[11.5px] text-[#6B6A65] mt-0.5">
              Foto, dati e descrizione saranno aggiunti a <span className="font-mono text-[10.5px]">{targetPropertyId}</span>. Niente duplicati.
            </div>
          </div>
        </div>
      )}
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#7C8B6E] font-semibold mb-2">
        {isStudio ? "Annuncio vocale" : "Sopralluogo Live"}
      </div>
      {isStudio ? (
        <DismissibleIntro storageKey="sopralluogo:studio-intro">
          <h1 className="text-[28px] lg:text-[34px] font-serif leading-[1.08] text-[#1C1C1A] mb-2">
            Detta il tuo annuncio.<br/>
            <span className="italic text-[#7C8B6E]">Le foto le aggiungi dopo.</span>
          </h1>
          <p className="text-[13.5px] text-[#6B6A65] leading-[1.55] mb-3">
            Sei in studio, in macchina, dal cliente. Parli, racconti quello che
            ricordi della casa, e Pronto
            <strong className="text-[#1C1C1A]"> trasforma il vocale in un annuncio</strong> con scheda compilata e prezzo di mercato.
          </p>
          <div className="bg-[#F1F4ED] border border-[#7C8B6E]/30 rounded-md px-3 py-2.5 mb-6 text-[12px] text-[#3F3D38] leading-snug">
            <strong className="text-[#7C8B6E]">3 step:</strong> 1. inizia · 2. parla (le foto sono opzionali) · 3. crea annuncio.
          </div>
        </DismissibleIntro>
      ) : (
        <DismissibleIntro storageKey="sopralluogo:live-intro">
          <h1 className="text-[28px] lg:text-[34px] font-serif leading-[1.08] text-[#1C1C1A] mb-2">
            Entri in casa, parli, scatti.<br/>
            <span className="italic text-[#7C8B6E]">Niente penna, niente blocco note.</span>
          </h1>
          <p className="text-[13.5px] text-[#6B6A65] leading-[1.55] mb-3">
            Cammina per le stanze, racconta cosa vedi e fotografa quello che ti
            serve. Alla fine Pronto ordina tutto per te e, con un tap,
            <strong className="text-[#1C1C1A]"> trasforma il sopralluogo in un annuncio</strong> con foto e dati già pronti.
          </p>
          <div className="bg-[#F1F4ED] border border-[#7C8B6E]/30 rounded-md px-3 py-2.5 mb-6 text-[12px] text-[#3F3D38] leading-snug">
            <strong className="text-[#7C8B6E]">3 step:</strong> 1. inizia · 2. parla e scatta · 3. crea annuncio (foto incluse, modificabili).
          </div>
        </DismissibleIntro>
      )}

      <div className="space-y-4 bg-white border border-[#E8E5DE] rounded-xl p-5 shadow-sm">
        <div className="text-[11.5px] text-[#5A6A4D] bg-[#F1F4ED] border border-[#7C8B6E]/25 rounded-md px-3 py-2 leading-snug">
          <strong>Tutto opzionale.</strong> Se vuoi premi subito <em>Inizia</em> e racconta città, indirizzo e tipologia nel vocale — Pronto li estrae da solo.
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-1.5">
            Città <span className="normal-case tracking-normal text-[#9A968B]/70 font-normal">(facoltativo)</span>
          </label>
          <input
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder={CITY_PLACEHOLDER}
            className="w-full px-3 py-2.5 bg-[#FAFAF7] border border-[#E8E5DE] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E] transition-colors"
            data-testid="sopralluogo-city-input"
          />
          <div className="text-[11px] text-[#9A968B] mt-1">Serve per stimare il prezzo di mercato (OMI).</div>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-1.5">
            Indirizzo <span className="normal-case tracking-normal text-[#9A968B]/70 font-normal">(facoltativo · serve per i servizi vicini)</span>
          </label>
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Via Garibaldi 12"
            className="w-full px-3 py-2.5 bg-[#FAFAF7] border border-[#E8E5DE] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E] transition-colors"
            data-testid="sopralluogo-address-input"
          />
          <div className="text-[11px] text-[#9A968B] mt-1">Aggiungiamo metro, scuole e supermercati vicini all'annuncio.</div>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-1.5">
            Tipologia <span className="normal-case tracking-normal text-[#9A968B]/70 font-normal">(facoltativo)</span>
          </label>
          <div className="grid grid-cols-3 gap-1.5">
            {PROPERTY_TYPES.map((p) => (
              <button
                key={p}
                onClick={() => setPtype(ptype === p ? null : p)}
                className={`px-2 py-1.5 rounded-md text-[12px] font-medium transition-all capitalize ${
                  ptype === p
                    ? "bg-[#1C1C1A] text-white"
                    : "bg-[#FAFAF7] text-[#3F3D38] border border-[#E8E5DE] hover:border-[#C8C5BD]"
                }`}
                data-testid={`sopralluogo-ptype-${p}`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
        <button
          onClick={start}
          disabled={starting}
          className="w-full bg-[#7C8B6E] hover:bg-[#6B7A5D] disabled:opacity-60 text-white font-medium py-3 rounded-md flex items-center justify-center gap-2 transition-colors"
          data-testid="sopralluogo-start-btn"
        >
          {starting ? <Spinner size={16} className="animate-spin" /> : <Microphone size={16} weight="fill" />}
          Inizia sopralluogo
        </button>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-3 text-center">
        {[
          ["Voce", "trascrizione automatica"],
          ["Foto", "classificate per stanza"],
          ["Fine", "immobile pronto"],
        ].map(([a, b], i) => (
          <div key={i} className="bg-white rounded-lg border border-[#E8E5DE] p-3">
            <div className="text-[12px] font-semibold text-[#1C1C1A]">{a}</div>
            <div className="text-[10.5px] text-[#9A968B] mt-0.5">{b}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
